import { canBeProject, projectProgress } from '@nedelnik/shared';
import { useState } from 'react';

import { ArrowUpRight, Plus, Times } from '../icons.js';
import { ProgressCount } from '../project/ProgressCount.js';
import { childrenOf, projectCandidates, projectOf } from '../project/projects.js';
import { TaskPicker } from '../search/TaskPicker.js';

import { blocks, dependsOn } from './links.js';

import type { CreateOutcome } from '../../core/actions.js';
import type { Task, TaskLink } from '@nedelnik/shared';

/**
 * Связи задачи одним разделом: проект, подзадачи, «зависит от», «блокирует»
 * (3.9.7).
 *
 * Раньше проект жил отдельным полем выше по карточке, а блокировки — своим
 * блоком внизу, с непохожей разметкой. Разница была исторической, а не
 * смысловой: во всех четырёх случаях вопрос один — «какая ещё задача с этим
 * связана», — и ответ на него подбирается одинаково. Пока блоки были разными,
 * они и расходились: в проекте можно было завести недостающую задачу, в
 * связях нельзя, хотя ситуация та же.
 *
 * Поэтому здесь один `RelationBlock` на все четыре раздела, а различаются они
 * только вопросом, набором кандидатов и тем, что делать с выбранным.
 *
 * Пустой раздел не показывается вовсе (принцип 6): четыре поля по две строки
 * делали из пустой карточки два с половиной экрана на телефоне. Вместо них —
 * ряд кнопок «добавить», по одной на каждый раздел, который для этой задачи
 * возможен; нажатие раскрывает раздел с уже открытым списком выбора.
 *
 * Поле подбора — только по этой кнопке, и у заполненного раздела тоже
 * (решение владельца, 2026-09-08). Раньше под списком связанного всегда
 * стояло поле «что держит эту задачу»: у задачи с тремя связями это три поля
 * впустую. Теперь раздел с содержимым — это заголовок и список, а добавить
 * ещё одну связь можно той же кнопкой в ряду «Связи»; после выбора поле
 * прячется обратно. У проекта кнопки нет, пока проект есть: их не бывает
 * двух (3.8.2).
 *
 * Уровней вложенности ровно два (3.8.1), и это единственное, что делает два
 * раздела взаимоисключающими: у задачи с вложенными нет проекта, у вложенной
 * задачи нет подзадач. Раньше об этом говорила подсказка «это сам проект» на
 * месте поля; теперь ряд «добавить» такой раздел просто не предлагает —
 * кнопки, которая никогда не сработает, в нём нет.
 */

/**
 * Почему задача не завелась. Формулировки те же, что в быстрой записи: один и
 * тот же отказ обязан звучать одинаково, где бы он ни случился.
 */
const CREATE_REFUSALS: Record<CreateOutcome, string> = {
  created: '',
  empty: 'Задаче нужно название, одного тега мало',
  'no-number': 'Номера задач ещё не приехали с сервера — нужна сеть',
};

export interface RelationActions {
  addLink: (blockerId: string, blockedId: string) => void;
  removeLink: (link: TaskLink) => void;
  setParent: (task: Task, parentId: string | null) => void;
  /** Завести задачу и сразу связать её с этой — стороной, которую задал блок. */
  createProject: (task: Task, name: string) => Promise<CreateOutcome>;
  createSubtask: (task: Task, name: string) => Promise<CreateOutcome>;
  createLinked: (task: Task, name: string, side: 'depends' | 'blocks') => Promise<CreateOutcome>;
}

export function Relations({
  task,
  tasks,
  links,
  byId,
  actions,
  onOpenTask,
  onOpenProject,
}: {
  task: Task;
  tasks: readonly Task[];
  links: readonly TaskLink[];
  byId: ReadonlyMap<string, Task>;
  actions: RelationActions;
  onOpenTask: (task: Task) => void;
  onOpenProject: (projectId: string) => void;
}): React.ReactElement {
  const depends = dependsOn(links, task.id, byId);
  const holding = blocks(links, task.id, byId);
  const parent = projectOf(byId, task);
  const children = childrenOf(tasks, task.id);

  // Уже связанное и сама задача из подборщика вычитаются: предлагать то, что
  // действие отвергнет как дубль, значит обещать несделанное.
  const linked = new Set([
    task.id,
    ...depends.map((side) => side.link.blocker_id),
    ...holding.map((side) => side.link.blocked_id),
  ]);
  const free = [...byId.values()].filter((candidate) => !linked.has(candidate.id));

  // Уровней ровно два: задача с вложенными — сама проект, и вложить её никуда
  // нельзя; вложенная задача, наоборот, не может стать проектом.
  const isProject = children.length > 0;
  const isNested = task.parent_id != null;

  // Разделы, у которых сейчас стоит поле подбора, — раскрытые нажатием
  // «добавить». Поле уходит, как только выбор сделан или человек ушёл из
  // поля; список связанного виден и без этого.
  const [opened, setOpened] = useState<ReadonlySet<Section>>(new Set());
  const open = (section: Section): void => setOpened((was) => new Set([...was, section]));
  const close = (section: Section): void =>
    setOpened((was) => {
      if (!was.has(section)) return was;
      const next = new Set(was);
      next.delete(section);
      return next;
    });

  const dependsItems: RelationItem[] = depends.map(({ link, other }) => ({
    key: link.id,
    other,
    onRemove: () => actions.removeLink(link),
    removeLabel: `Снять связь: зависит от «${other?.title ?? 'удалённая задача'}»`,
  }));

  const holdingItems: RelationItem[] = holding.map(({ link, other }) => ({
    key: link.id,
    other,
    onRemove: () => actions.removeLink(link),
    removeLabel: `Снять связь: блокирует «${other?.title ?? 'удалённая задача'}»`,
  }));

  // `== null`, а не `=== null`: отсутствующее поле — это «проекта нет», а не
  // «проект удалён». Со строгим сравнением задача, у которой `parent_id` не
  // доехал в локальную копию, показывала бы удалённый проект — сообщение о
  // том, чего не было.
  const parentItems: RelationItem[] =
    parent === undefined
      ? // `parent_id` может указывать на надгробие: проект удалили с другого
        // устройства. Задача при этом ничьей не стала — ссылка в базе
        // осталась, и снять её должно быть чем.
        task.parent_id == null
        ? []
        : [
            {
              key: 'gone',
              other: undefined,
              onRemove: () => actions.setParent(task, null),
              removeLabel: 'Убрать из проекта',
            },
          ]
      : [
          {
            key: parent.id,
            other: parent,
            badge: <ProgressCount progress={projectProgress(parent.id, tasks)} />,
            onRemove: () => actions.setParent(task, null),
            removeLabel: 'Убрать из проекта',
          },
        ];

  const childItems: RelationItem[] = children.map((child) => ({
    key: child.id,
    other: child,
    onRemove: () => actions.setParent(child, null),
    removeLabel: `Вынуть из проекта: «${child.title ?? ''}»`,
  }));

  /** Раздел показан, если в нём что-то есть или у него сейчас стоит поле. */
  const shown = (section: Section, items: readonly RelationItem[]): boolean =>
    items.length > 0 || opened.has(section);

  // Что можно добавить: разделы без поля, кроме тех, что правило уровней
  // запрещает этой задаче, и кроме проекта, когда он уже есть — второго не
  // бывает (3.8.2). Связей и подзадач бывает сколько угодно, у них кнопка
  // остаётся и при заполненном разделе.
  const sections: {
    section: Section;
    title: string;
    items: RelationItem[];
    locked: boolean;
    single: boolean;
  }[] = [
    { section: 'depends', title: 'Зависит от', items: dependsItems, locked: false, single: false },
    { section: 'blocks', title: 'Блокирует', items: holdingItems, locked: false, single: false },
    { section: 'project', title: 'Проект', items: parentItems, locked: isProject, single: true },
    { section: 'children', title: 'Подзадачи', items: childItems, locked: isNested, single: false },
  ];
  const addable = sections.filter(
    (entry) =>
      !entry.locked && !opened.has(entry.section) && (!entry.single || entry.items.length === 0),
  );

  return (
    <div className="rel">
      {shown('depends', dependsItems) && (
        <RelationBlock
          title="Зависит от"
          hint="что держит эту задачу"
          items={dependsItems}
          candidates={free}
          picker={opened.has('depends')}
          onPick={(chosen) => actions.addLink(chosen.id, task.id)}
          onCreate={(name) => actions.createLinked(task, name, 'depends')}
          createLabel={(name) => `Завести «${name}» и поставить блокирующей`}
          onDismiss={() => close('depends')}
          onOpen={onOpenTask}
        />
      )}

      {shown('blocks', holdingItems) && (
        <RelationBlock
          title="Блокирует"
          hint="какую задачу держит эта"
          items={holdingItems}
          candidates={free}
          picker={opened.has('blocks')}
          onPick={(chosen) => actions.addLink(task.id, chosen.id)}
          onCreate={(name) => actions.createLinked(task, name, 'blocks')}
          createLabel={(name) => `Завести «${name}» и заблокировать её`}
          onDismiss={() => close('blocks')}
          onOpen={onOpenTask}
        />
      )}

      {shown('project', parentItems) && (
        <RelationBlock
          title="Проект"
          hint="в какой проект вложить"
          items={parentItems}
          // Вычтены сама задача, её потомки и уже вложенные задачи: подборщик
          // не показывает вариант, который действие всё равно отвергнет.
          candidates={projectCandidates(tasks, task.id)}
          picker={opened.has('project')}
          onPick={(chosen) => actions.setParent(task, chosen.id)}
          onCreate={(name) => actions.createProject(task, name)}
          createLabel={(name) => `Завести проект «${name}» и вложить сюда`}
          onDismiss={() => close('project')}
          // Переход к проекту целиком — его задачи из всех спринтов (3.8.8).
          onOpen={(chosen) => onOpenProject(chosen.id)}
        />
      )}

      {shown('children', childItems) && (
        <RelationBlock
          title="Подзадачи"
          // Доля закрытых видна и внутри карточки (3.8.5): снаружи она стоит
          // у самой задачи-проекта, а здесь отвечает на тот же вопрос, не
          // заставляя считать пункты списка глазами.
          badge={
            isProject ? <ProgressCount progress={projectProgress(task.id, tasks)} /> : undefined
          }
          hint="какую задачу вложить сюда"
          items={childItems}
          // Кандидат не должен быть проектом сам и не должен уже где-то
          // лежать: правило уровней спрашивается у `shared`, а не
          // переписывается здесь.
          candidates={tasks.filter(
            (candidate) =>
              candidate.id !== task.id && candidate.parent_id == null && canBeProject(task),
          )}
          picker={opened.has('children')}
          onPick={(chosen) => actions.setParent(chosen, task.id)}
          onCreate={(name) => actions.createSubtask(task, name)}
          createLabel={(name) => `Завести «${name}» и вложить сюда`}
          onDismiss={() => close('children')}
          onOpen={onOpenTask}
        />
      )}

      {addable.length > 0 && (
        // Имя кнопки — название раздела: она и есть раздел, только свёрнутый.
        <div className="field">
          <span className="field__label">Связи</span>
          <div className="rel__adds" role="group" aria-label="Добавить связь">
            {addable.map(({ section, title }) => (
              <button
                key={section}
                type="button"
                className="quickbtn plate"
                onClick={() => open(section)}
              >
                <Plus />
                {title}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

type Section = 'depends' | 'blocks' | 'project' | 'children';

interface RelationItem {
  key: string;
  /** Связанная задача или ничего: её могли удалить с другого устройства. */
  other: Task | undefined;
  badge?: React.ReactNode;
  onRemove: () => void;
  removeLabel: string;
}

/**
 * Один раздел связей: заголовок, список связанного и — пока его ждут — поле
 * подбора.
 *
 * Показывается только с содержимым или по нажатию «добавить» (см. выше):
 * подсказки вроде «ничто не держит» на месте пустого раздела повторяли
 * заголовок и занимали строку, ничего не сообщая. Поле стоит только по
 * нажатию и уходит после выбора или когда из него ушли.
 */
function RelationBlock({
  title,
  badge,
  hint,
  items,
  candidates,
  picker,
  onPick,
  onCreate,
  createLabel,
  onDismiss,
  onOpen,
}: {
  title: string;
  /** Что показать у заголовка: доля закрытых у проекта. */
  badge?: React.ReactNode;
  hint: string;
  items: readonly RelationItem[];
  candidates: readonly Task[];
  /** Поле подбора сейчас нужно: его раскрыли нажатием, и список виден сразу. */
  picker: boolean;
  onPick: (chosen: Task) => void;
  onCreate: (name: string) => Promise<CreateOutcome>;
  createLabel: (name: string) => string;
  /** Поле больше не нужно: выбор сделан или человек ушёл. */
  onDismiss: () => void;
  onOpen: (task: Task) => void;
}): React.ReactElement {
  const [notice, setNotice] = useState<string | undefined>(undefined);

  return (
    <div className="field">
      <span className="field__label">
        {title}
        {badge}
      </span>

      {items.length > 0 && (
        // Список подписан своим разделом: на экране их четыре, и в озвучке
        // безымянные списки не отличить друг от друга.
        <ul className="links__list" aria-label={title}>
          {items.map((item) => (
            <li key={item.key} className="links__item">
              {item.other === undefined ? (
                // Задачу удалили с другого устройства. Молчать нельзя: связь в
                // базе есть, и человек должен видеть, что именно он снимает.
                <span className="links__gone">задача удалена</span>
              ) : (
                <button
                  type="button"
                  className="links__link"
                  onClick={() => {
                    if (item.other !== undefined) onOpen(item.other);
                  }}
                >
                  {/*
                    Стрелка «↗» говорит «перейти к задаче». Без неё название
                    читалось подписью, а не ссылкой, и в голову не приходило
                    на него нажать (замечание владельца, 2026-09-13).
                  */}
                  <ArrowUpRight />
                  {item.other.status === 'done' && (
                    <i className="dot dot--done" aria-hidden="true" />
                  )}
                  {/* Обёртка ради многоточия: во flex-контейнере голый текст
                      обрезается без него, и «Переезд в новую квартиру»
                      упирался в бейдж прогресса огрызком. */}
                  <span className="links__title">{item.other.title}</span>
                </button>
              )}
              {item.badge}
              <button
                type="button"
                className="iconbtn iconbtn--danger"
                aria-label={item.removeLabel}
                onClick={item.onRemove}
              >
                <Times />
              </button>
            </li>
          ))}
        </ul>
      )}

      {picker && (
        <TaskPicker
          inline
          initiallyOpen
          label={`Найти задачу: ${title.toLowerCase()}`}
          addLabel={`Добавить: ${title.toLowerCase()}`}
          placeholder={hint}
          candidates={candidates}
          // Выбор сделан — поле своё отработало и уходит; следующую связь
          // добавляют той же кнопкой в ряду «Связи».
          onPick={(chosen) => {
            onPick(chosen);
            onDismiss();
          }}
          // Нужной задачи может ещё не быть, и без этого связь заводится задом
          // наперёд: закрыть карточку, записать задачу строкой быстрой записи,
          // открыть карточку заново и только тогда связать.
          //
          // Отказ не закрывает поле: стереть набранное и не сказать, почему
          // ничего не произошло, — худшее из возможных поведений.
          onCreate={(name) => {
            void onCreate(name).then(
              (outcome) => {
                if (outcome === 'created') onDismiss();
                else setNotice(CREATE_REFUSALS[outcome]);
              },
              () => setNotice('Задача не завелась — попробуйте ещё раз'),
            );
          }}
          createLabel={createLabel}
          notice={notice}
          onCancel={onDismiss}
        />
      )}
    </div>
  );
}
